// src/components/PreviewErrorBoundary.tsx
// Specialized error boundary for the preview iframe and sandbox
import * as React from "react";
import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { safeError } from "@/lib/safeError";
import { Button } from "@/components/ui/button";
import { Monitor, RefreshCw, Code } from "lucide-react";

interface Props {
  children: ReactNode;
  sessionId?: string;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

export class PreviewErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const info = safeError(error);
    return { hasError: true, errorMessage: info.message };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    const info = safeError(error);
    console.error("[PreviewErrorBoundary]", info.name, info.message);

    // Send to Sentry with preview context
    Sentry.withScope((scope) => {
      scope.setTag("errorType", "preview_crash");
      scope.setTag("component", "PreviewPane");
      if (this.props.sessionId) {
        scope.setExtra("sessionId", this.props.sessionId);
      }
      scope.setExtra("componentStack", errorInfo.componentStack);
      Sentry.captureException(info.raw);
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorMessage: "" });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex-1 flex items-center justify-center bg-[#0a0a0f] border border-gray-800 rounded-lg p-6">
          <div className="text-center space-y-4 max-w-sm">
            <div className="mx-auto w-14 h-14 rounded-xl bg-red-500/10 flex items-center justify-center">
              <Monitor className="h-7 w-7 text-red-400" />
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-white">
                Preview failed to load
              </h3>
              <p className="text-sm text-gray-400">
                {this.state.errorMessage || "The preview encountered an error."}
              </p>
            </div>

            <div className="flex gap-2 justify-center">
              <Button
                onClick={this.handleRetry}
                size="sm"
                className="gap-2 bg-indigo-600 hover:bg-indigo-700"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Retry
              </Button>
            </div>

            <p className="text-xs text-gray-600">
              Check the console for build errors
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default PreviewErrorBoundary;
